import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  adminAddCredits,
  adminChangePlan,
  adminConfirmPayment,
  adminUpdateCoefficient,
  getClientFull,
  listClientProductions,
  listClientTransactions,
} from '../../api/admin';
import type {
  Client,
  CreditTransaction,
  PlanTier,
  Production,
  SubscriptionStatus,
} from '../../types/db';

const planTiers: PlanTier[] = ['solo', 'studio', 'network', 'group', 'enterprise'];

function statusBadgeClass(status: SubscriptionStatus | null): string {
  switch (status) {
    case 'active':
      return 'bg-emerald-50 text-emerald-700';
    case 'past_due':
      return 'bg-amber-50 text-amber-700';
    case 'canceled':
      return 'bg-rose-50 text-rose-700';
    case 'paused':
      return 'bg-slate-100 text-slate-600';
    default:
      return 'bg-slate-100 text-slate-600';
  }
}

type ModalType = 'payment' | 'credits' | 'plan' | 'coefficient' | null;

export default function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [client, setClient] = useState<Client | null>(null);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [productions, setProductions] = useState<Production[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState<ModalType>(null);
  const [submitting, setSubmitting] = useState(false);

  // Form states
  const [paymentNotes, setPaymentNotes] = useState('');
  const [creditsAmount, setCreditsAmount] = useState('');
  const [creditsNotes, setCreditsNotes] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<PlanTier>('solo');
  const [coefficientMap, setCoefficientMap] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const [c, tx, prods] = await Promise.all([
          getClientFull(id!),
          listClientTransactions(id!, 10),
          listClientProductions(id!),
        ]);
        if (!cancelled) {
          setClient(c);
          setTransactions(tx);
          setProductions(prods);
          if (c?.plan_tier) setSelectedPlan(c.plan_tier);
          const map: Record<string, string> = {};
          prods.forEach((p) => {
            map[p.id] = String(p.coefficient_credit ?? 1);
          });
          setCoefficientMap(map);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Errore caricamento cliente');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleConfirmPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setSubmitting(true);
    try {
      await adminConfirmPayment(id, paymentNotes || undefined);
      setModalOpen(null);
      setPaymentNotes('');
      // Ricarica transazioni
      const tx = await listClientTransactions(id, 10);
      setTransactions(tx);
      const c = await getClientFull(id);
      setClient(c);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Errore');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddCredits = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    const amount = parseFloat(creditsAmount);
    if (Number.isNaN(amount) || amount <= 0) {
      alert('Inserisci un importo valido');
      return;
    }
    setSubmitting(true);
    try {
      await adminAddCredits(id, amount, creditsNotes || undefined);
      setModalOpen(null);
      setCreditsAmount('');
      setCreditsNotes('');
      const tx = await listClientTransactions(id, 10);
      setTransactions(tx);
      const c = await getClientFull(id);
      setClient(c);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Errore');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setSubmitting(true);
    try {
      await adminChangePlan(id, selectedPlan);
      setModalOpen(null);
      const c = await getClientFull(id);
      setClient(c);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Errore');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateCoefficient = async (productionId: string) => {
    const val = parseFloat(coefficientMap[productionId] ?? '1');
    if (Number.isNaN(val) || val <= 0) {
      alert('Coefficiente non valido');
      return;
    }
    setSubmitting(true);
    try {
      await adminUpdateCoefficient(productionId, val);
      const prods = await listClientProductions(id!);
      setProductions(prods);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Errore');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-rose-700">
        <p className="font-medium">Errore</p>
        <p className="text-sm">{error ?? 'Cliente non trovato'}</p>
        <button
          onClick={() => navigate('/admin')}
          className="mt-4 text-sm font-medium text-brand-600 hover:text-brand-700"
        >
          Torna alla lista
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <button
            onClick={() => navigate('/admin')}
            className="text-sm text-slate-500 hover:text-slate-700"
          >
            ← Torna ai clienti
          </button>
          <h1 className="mt-2 text-2xl font-bold text-slate-900">{client.name}</h1>
          <div className="mt-1 flex items-center gap-2">
            <span
              className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(
                client.subscription_status,
              )}`}
            >
              {client.subscription_status ?? '—'}
            </span>
            <span className="text-sm text-slate-500 capitalize">
              Piano {client.plan_tier ?? '—'}
            </span>
          </div>
        </div>
      </div>

      {/* Sticky actions */}
      <div className="sticky top-0 z-10 -mx-4 border-b border-slate-200 bg-white/80 px-4 py-3 backdrop-blur md:mx-0 md:rounded-xl md:border md:px-4">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setModalOpen('payment')}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
          >
            Conferma pagamento
          </button>
          <button
            onClick={() => setModalOpen('credits')}
            className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200 hover:bg-slate-50"
          >
            Aggiungi crediti
          </button>
          <button
            onClick={() => setModalOpen('plan')}
            className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200 hover:bg-slate-50"
          >
            Cambia piano
          </button>
          <button
            onClick={() => setModalOpen('coefficient')}
            className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200 hover:bg-slate-50"
          >
            Modifica coefficienti
          </button>
        </div>
      </div>

      {/* Info grid */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium text-slate-500">ID Stripe</p>
          <p className="mt-1 text-sm text-slate-900">{client.stripe_customer_id ?? '—'}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium text-slate-500">ID Subscription</p>
          <p className="mt-1 text-sm text-slate-900">{client.stripe_subscription_id ?? '—'}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium text-slate-500">Periodo corrente</p>
          <p className="mt-1 text-sm text-slate-900">
            {client.current_period_start
              ? new Date(client.current_period_start).toLocaleDateString('it-IT')
              : '—'}{' '}
            →{' '}
            {client.current_period_end
              ? new Date(client.current_period_end).toLocaleDateString('it-IT')
              : '—'}
          </p>
        </div>
      </div>

      {/* Testate */}
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900">Testate</h2>
        {productions.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">Nessuna testata per questo cliente.</p>
        ) : (
          <div className="mt-3 divide-y divide-slate-100">
            {productions.map((p) => (
              <div key={p.id} className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm font-medium text-slate-900">{p.brand_name || p.name}</p>
                  <p className="text-xs text-slate-500">
                    {p.slug} · Coefficiente: {p.coefficient_credit ?? 1}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    step="0.1"
                    min="0.1"
                    value={coefficientMap[p.id] ?? '1'}
                    onChange={(e) =>
                      setCoefficientMap((prev) => ({ ...prev, [p.id]: e.target.value }))
                    }
                    className="w-20 rounded-lg border border-slate-300 px-2 py-1 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                  <button
                    onClick={() => handleUpdateCoefficient(p.id)}
                    disabled={submitting}
                    className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
                  >
                    Salva
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Storico transazioni */}
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Storico transazioni</h2>
          <span className="text-xs text-slate-500">Ultime 10</span>
        </div>
        {transactions.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">Nessuna transazione.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200">
                <tr>
                  <th className="py-2 pr-4 font-semibold text-slate-700">Data</th>
                  <th className="py-2 pr-4 font-semibold text-slate-700">Tipo</th>
                  <th className="py-2 pr-4 font-semibold text-slate-700">Importo</th>
                  <th className="py-2 pr-4 font-semibold text-slate-700">Descrizione</th>
                  <th className="py-2 font-semibold text-slate-700">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {transactions.map((tx) => (
                  <tr key={tx.id}>
                    <td className="py-2 pr-4 text-slate-600">
                      {new Date(tx.created_at).toLocaleDateString('it-IT')}
                    </td>
                    <td className="py-2 pr-4">
                      <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                        {tx.type}
                      </span>
                    </td>
                    <td className="py-2 pr-4 font-medium text-slate-900">{tx.amount}</td>
                    <td className="py-2 pr-4 text-slate-600">{tx.description ?? '—'}</td>
                    <td className="py-2 text-slate-500">{tx.admin_notes ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Stripe placeholder */}
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900">Stripe</h2>
        <p className="mt-2 text-sm text-slate-500">
          Integrazione Stripe in arrivo con Sprint Pricing/Billing. Qui verranno mostrati i metodi
          di pagamento, le fatture e i link al Customer Portal.
        </p>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="rounded-lg bg-slate-50 p-3">
            <p className="text-xs font-medium text-slate-500">Metodo di pagamento</p>
            <p className="mt-1 text-sm text-slate-400">—</p>
          </div>
          <div className="rounded-lg bg-slate-50 p-3">
            <p className="text-xs font-medium text-slate-500">Ultima fattura</p>
            <p className="mt-1 text-sm text-slate-400">—</p>
          </div>
        </div>
      </section>

      {/* Modals */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            {/* Conferma pagamento */}
            {modalOpen === 'payment' && (
              <form onSubmit={handleConfirmPayment} className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900">Conferma pagamento</h3>
                <p className="text-sm text-slate-500">
                  Conferma il pagamento per rinnovare il periodo corrente del cliente.
                </p>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Note (opzionale)</label>
                  <textarea
                    value={paymentNotes}
                    onChange={(e) => setPaymentNotes(e.target.value)}
                    rows={3}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setModalOpen(null)}
                    className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
                  >
                    {submitting ? 'Invio...' : 'Conferma'}
                  </button>
                </div>
              </form>
            )}

            {/* Aggiungi crediti */}
            {modalOpen === 'credits' && (
              <form onSubmit={handleAddCredits} className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900">Aggiungi crediti</h3>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Minuti da aggiungere</label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    required
                    value={creditsAmount}
                    onChange={(e) => setCreditsAmount(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Note (opzionale)</label>
                  <textarea
                    value={creditsNotes}
                    onChange={(e) => setCreditsNotes(e.target.value)}
                    rows={2}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setModalOpen(null)}
                    className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
                  >
                    {submitting ? 'Invio...' : 'Aggiungi'}
                  </button>
                </div>
              </form>
            )}

            {/* Cambia piano */}
            {modalOpen === 'plan' && (
              <form onSubmit={handleChangePlan} className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900">Cambia piano</h3>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Nuovo piano</label>
                  <select
                    value={selectedPlan}
                    onChange={(e) => setSelectedPlan(e.target.value as PlanTier)}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  >
                    {planTiers.map((t) => (
                      <option key={t} value={t}>
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setModalOpen(null)}
                    className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
                  >
                    {submitting ? 'Invio...' : 'Cambia piano'}
                  </button>
                </div>
              </form>
            )}

            {/* Modifica coefficienti */}
            {modalOpen === 'coefficient' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900">Modifica coefficienti</h3>
                <p className="text-sm text-slate-500">
                  Modifica il coefficiente credit per ogni testata. 1 = standard, &gt;1 costa di più,
                  &lt;1 costa di meno.
                </p>
                <div className="max-h-64 space-y-2 overflow-y-auto">
                  {productions.map((p) => (
                    <div key={p.id} className="flex items-center gap-2">
                      <span className="flex-1 text-sm text-slate-700">{p.brand_name || p.name}</span>
                      <input
                        type="number"
                        step="0.1"
                        min="0.1"
                        value={coefficientMap[p.id] ?? '1'}
                        onChange={(e) =>
                          setCoefficientMap((prev) => ({ ...prev, [p.id]: e.target.value }))
                        }
                        className="w-20 rounded-lg border border-slate-300 px-2 py-1 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                      <button
                        onClick={() => handleUpdateCoefficient(p.id)}
                        disabled={submitting}
                        className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
                      >
                        Salva
                      </button>
                    </div>
                  ))}
                  {productions.length === 0 && (
                    <p className="text-sm text-slate-500">Nessuna testata disponibile.</p>
                  )}
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => setModalOpen(null)}
                    className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
                  >
                    Chiudi
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
